import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const initialState = {
  sales: [],
  allSales: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    recordsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false
  },
  loading: false,
  allSalesLoading: false,
  error: null,
};

// Create new sale
export const addSale = createAsyncThunk('sales/addSale', async (saleData, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.userInfo.token;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.post('/api/sales', saleData, config);
    return data;
  } catch (error) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

// Get user sales
export const getMySales = createAsyncThunk('sales/getMySales', async (_, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.userInfo.token;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.get('/api/sales/mystaffsales', config);
    return data;
  } catch (error) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

// Get all sales with pagination
export const getAllSales = createAsyncThunk('sales/getAllSales', async ({ page = 1, limit = 10 }, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.userInfo.token;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.get(`/api/sales/all?page=${page}&limit=${limit}`, config);
    return data;
  } catch (error) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

// Update sale (Admin only)
export const updateSale = createAsyncThunk('sales/updateSale', async ({ id, saleData }, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.userInfo.token;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.put(`/api/sales/${id}`, saleData, config);
    return data;
  } catch (error) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

// Delete sale (Admin only)
export const deleteSale = createAsyncThunk('sales/deleteSale', async (id, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.userInfo.token;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    await axios.delete(`/api/sales/${id}`, config);
    return id;
  } catch (error) {
    const message =
      (error.response && error.response.data && error.response.data.message) ||
      error.message ||
      error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const saleSlice = createSlice({
  name: 'sale',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(addSale.pending, (state) => {
        state.loading = true;
      })
      .addCase(addSale.fulfilled, (state, action) => {
        state.loading = false;
        state.sales.push(action.payload);
      })
      .addCase(addSale.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getMySales.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMySales.fulfilled, (state, action) => {
        state.loading = false;
        state.sales = action.payload;
      })
      .addCase(getMySales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getAllSales.pending, (state) => {
        state.allSalesLoading = true;
      })
      .addCase(getAllSales.fulfilled, (state, action) => {
        state.allSalesLoading = false;
        state.allSales = action.payload.sales;
        state.pagination = action.payload.pagination;
      })
      .addCase(getAllSales.rejected, (state, action) => {
        state.allSalesLoading = false;
        state.error = action.payload;
      })
      .addCase(updateSale.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateSale.fulfilled, (state, action) => {
        state.loading = false;
        // Update the sale in allSales array
        const index = state.allSales.findIndex(sale => sale._id === action.payload._id);
        if (index !== -1) {
          state.allSales[index] = action.payload;
        }
        // Update the sale in sales array if it exists
        const salesIndex = state.sales.findIndex(sale => sale._id === action.payload._id);
        if (salesIndex !== -1) {
          state.sales[salesIndex] = action.payload;
        }
      })
      .addCase(updateSale.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteSale.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteSale.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the sale from allSales array
        state.allSales = state.allSales.filter(sale => sale._id !== action.payload);
        // Remove the sale from sales array
        state.sales = state.sales.filter(sale => sale._id !== action.payload);
      })
      .addCase(deleteSale.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default saleSlice.reducer;
